// Importing the `Search` icon from the `lucide-react` library
import { Search } from 'lucide-react';

// Defining the props for the HeroSection component
interface HeroSectionProps {
  searchTitle: string; // The current search title input value
  searchLocation: string; // The current search location input value (not used here but part of the props)
  onSearchTitleChange: (value: string) => void; // Callback to handle changes to the search title input
  onSearchLocationChange: (value: string) => void; // Callback to handle changes to the search location input (not used here)
  onSearch: () => void; // Callback to trigger the search action
}

// Functional component for the Hero Section
export function HeroSection({
  searchTitle,
  onSearchTitleChange,
  onSearch,
}: HeroSectionProps) {
  return (
    <header className="relative bg-[var(--color-secondary-main)] text-white py-12 lg:py-20">
      {/* Container for layout and responsiveness */}
      <div className="container mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-center gap-12">
        
        {/* Left Section */}
        <div className="flex-1 text-center lg:text-left">
          <h1 className="text-5xl text-white font-bold mb-6">
            Vous aimez la nature, <br /> ici un job pour vous !
          </h1>
          <p className="text-lg mb-8">
            Toutes les offres d'emploi des métiers du paysage.
          </p>

          {/* Search Form */}
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Quel poste recherchez-vous ?"
                  value={searchTitle}
                  onChange={(e) => onSearchTitleChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[var(--color-primary-main)] focus:ring-2 focus:ring-[var(--color-primary-main)] text-gray-700"
                />
              </div>

              {/* Search Button */}
              <button
                onClick={onSearch}
                className="px-8 py-3 rounded-lg bg-[var(--color-primary-dark)] text-white font-semibold hover:bg-[var(--color-secondary-main)] transition-colors duration-200"
              >
                Rechercher
              </button>
            </div>

            {/* Popular Searches */}
            <div className="mt-4 flex flex-wrap gap-2 text-sm text-gray-600">
              <span className="text-gray-500">Recherches populaires :</span>
              {['Jardinier', 'Paysagiste', 'Chef de projet', 'Élagueur'].map((term) => (
                <button
                  key={term}
                  onClick={() => onSearchTitleChange(term)}
                  className="hover:text-[var(--color-primary-main)] transition-colors duration-200"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex-1 relative">
          <div className="relative rounded-xl overflow-hidden">
            {/* Hero Image */}
            <img
              src="./pays.png"
              alt="Paysagisme professionnel"
              className="rounded-xl object-cover"
            />
            {/* Overlay Text */}
            <div className="absolute bottom-4 left-4 bg-white p-4 rounded-lg shadow-lg">
              <p className="text-xl font-semibold text-[var(--color-primary-dark)]">+100</p>
              <p className="text-sm text-gray-600">Offres d'emploi</p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
